<template>
  <div class="worldAreaList">
    <div
      v-for="area, i in displayList"
      ref="block"
      :key="area.areaId"
      class="block"
      :data-area="area.areaId"
    >
      <div
        class="head"
        @click.prevent="accordion(i)"
      >
        <p class="area">
          {{ area.name }}
        </p>
        <button class="button" />
      </div>
      <div
        class="inner"
      >
        <div class="content">
          <template
            v-for="country in area.countryList"
            :key="country.countryId"
          >
            <NuxtLink
              v-if="!isCountry || country.countryId !== countryId"
              :to="getPathLink(area.areaId, country.countryId)"
              :external="isExternalLink"
              class="link"
            >
              <figure class="flag">
                <img
                  :src="`${runtimeConfig.public.gvsUrl}/onebox/img/world/flag/${country.cc}.webp`"
                  width="20"
                  height="15"
                  alt=""
                  loading="lazy"
                >
              </figure>
              <p class="name">
                {{ country.label }}
              </p>
            </NuxtLink>
            <div
              v-else
              class="link"
              @click="scrollToTop"
            >
              <figure class="flag">
                <img
                  :src="`${runtimeConfig.public.gvsUrl}/onebox/img/world/flag/${country.cc}.webp`"
                  width="20"
                  height="15"
                  alt=""
                  loading="lazy"
                >
              </figure>
              <p class="name">
                {{ country.label }}
              </p>
            </div>
          </template>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import areaCountryList from '~/data/table/area_country_list.json'
import countryIdList from '~/data/table/country_id_list.json'

const runtimeConfig = useRuntimeConfig()
const { $isWebViewFlag: isWebViewFlag } = useNuxtApp()
const isExternalLink = ref(isWebViewFlag)

const props = defineProps({
  isRadar: {
    type: Boolean,
    required: false,
    default: false
  },
  isCountry: {
    type: Boolean,
    required: false,
    default: false
  }
})

const blockRefs = useTemplateRef('block')

const route = useRoute()
const countryId = route.params.country

const displayList = Object.keys(areaLabelMap).map((areaId) => {
  const countryList = []
  areaCountryList[areaId].countries.forEach((country) => {
    if (country.country_id === 'all') return
    countryList.push({
      countryId: country.country_id,
      label: country.label,
      cc: countryIdList[`/${areaId}/${country.country_id}/`].cc
    })
  })
  return {
    name: areaLabelMap[areaId],
    areaId,
    isOpen: ref(false),
    countryList
  }
})

const accordion = (index) => {
  const target = blockRefs.value[index]
  const isOpen = target.classList.contains('isOpen')
  if (isOpen) {
    target.classList.remove('isOpen')
    target.querySelector('.inner').style.height = 0
  } else {
    blockRefs.value.forEach((block) => {
      block.classList.remove('isOpen')
      block.querySelector('.inner').style.height = 0
    })
    const inner = target.querySelector('.inner')
    const contentH = target.querySelector('.content').clientHeight

    target.classList.add('isOpen')
    inner.style.height = contentH + 'px'
  }
}

const scrollToTop = () => {
  window.scrollTo({ top: 0, behavior: 'smooth' })
}

const getPathLink = (areaId, countryId) => {
  let relPath = `${areaId}/${countryId}/`
  if (props.isRadar) {
    relPath = `radar/${relPath}`
  }
  return isExternalLink.value ? `${runtimeConfig.app.baseURL}${relPath}` : `/${relPath}`
}
</script>

<style lang="scss" scoped>
.worldAreaList {
  display: flex;
  flex-direction: column;
  gap: 10px;
  .block {
    box-shadow: 0 4px 4px 0 rgb(0 0 0 / .25);
    border-radius: 6px;
    border-style: solid;
    border-width: 2px;
    .button {
      cursor: pointer;
    }
    &[data-area="5"] {
      border-color: #527E23;
      .button {
        background-color: #527E23;
        border-color: #527E23;
      }
    }
    &[data-area="9"] {
      border-color: #C77928;
      .button {
        background-color: #C77928;
        border-color: #C77928;
      }
    }
    &[data-area="1"],
    &[data-area="2"],
    &[data-area="3"],
    &[data-area="4"] {
      border-color: #4577CB;
      .button {
        background-color: #4577CB;
        border-color: #4577CB;
      }
    }
    &[data-area="6"] {
      border-color: #BA507E;
      .button {
        background-color: #BA507E;
        border-color: #BA507E;
      }
    }
    &[data-area="7"] {
      border-color: #986EB1;
      .button {
        background-color: #986EB1;
        border-color: #986EB1;
      }
    }
    &[data-area="8"] {
      border-color: #A89B2B;
      .button {
        background-color: #A89B2B;
        border-color: #A89B2B;
      }
    }
  }
  .head {
    position: relative;
    display: flex;
    justify-content: space-between;
    align-items: center;
    height: 36px;
    border-radius: 6px;
    background-color: #fff;
    cursor: pointer;
  }
  .area {
    flex: 1;
    padding-inline: 15px;
    color: #000;
    font-size: 16px;
    font-weight: 600;
    line-height: 1em;
    letter-spacing: .25px;
    user-select: none;
  }
  .button {
    position: relative;
    width: 36px;
    height: 100%;
    background-color: #fff;
    border-left: solid 2px;
    &:is(.isOpen *) {
      &::after {
        transform: translate(-50%, -50%) rotate(180deg);
        opacity: 0;
      }
    }
    &::before,
    &::after {
      position: absolute;
      top: 50%;
      left: 50%;
      transform: translate(-50%, -50%);
      width: 18px;
      height: 3px;
      border-radius: 100vmax;
      background-color: #fff;
      content: '';
    }
    &::after {
      transform: translate(-50%, -50%) rotate(90deg);
      transition: transform .2s ease-in-out, opacity .2s ease-in-out;
    }
  }
  .inner {
    height: 0;
    overflow: hidden;
    transition: height .2s ease-in-out;
  }
  .content {
    display: flex;
    flex-wrap: wrap;
    padding: 20px;
    gap: 8px;
    border-bottom-left-radius: 6px;
    border-bottom-right-radius: 6px;
    background-color: #EEF1F8;
  }
  .link {
    display: flex;
    align-items: center;
    gap: 3px;
    background-color: #fff;
    border-radius: 3px;
    padding: 3px 8px;
    box-shadow: 0 2px 4px 0 rgb(0 0 0 / .15);
  }
  .flag {
    aspect-ratio: 1;
    border: 1px solid #ccc;
    border-radius: 50%;
    overflow: hidden;
    img {
      width: 20px;
      display: block;
      filter: saturate(60%) brightness(130%);
      height: 100%;
      object-fit: cover;
      vertical-align: middle;
    }
  }
  .name {
    color: #3569c0;
    font-size: 14px;
    // TODO: tmp
    font-weight: 600;
    line-height: 22px;
    text-decoration: underline;
  }
  @media (any-hover: hover) {
    .block {
      transition: box-shadow .2s ease-in-out;
      &:hover {
        box-shadow: 0 4px 4px 1px rgb(0 0 0 / .5);
      }
    }
    .link {
      transition: box-shadow .2s ease-in-out;
      &:hover {
        box-shadow: 0 2px 6px 0 rgba(#000, .45);
      }
    }
  }
}
</style>
